import { Box } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { CSSProperties, ReactElement, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMarketOptions } from '@/hooks/useMarketOptions';
import { useCalendarDay, useNow } from '@/hooks/useNow';
import { alert } from '@/services/alert';
import { getRateLimitAlert, isMedplumRateLimitError } from '@/utils/medplumRateLimit';
import { FINANCING_PATIENTS_QUERY } from '../../graphql';
import { useHubFilterValues } from '../../hooks/useHubFilterValues';
import { usePacQueueHydration } from '../../hooks/usePacQueueHydration';
import { usePatientListFilters } from '../../hooks/usePatientListFilters';
import { QUEUE_PAGE_ROWS, useQueuePage } from '../../hooks/useQueuePage';
import {
  buildFinancingFilter,
  FINANCING_STEP_OPTIONS,
  financingSubscriptionParams,
  matchesFinancingStep,
} from '../../services/financingFilters';
import { buildFinancingRows, FinancingLeadRow } from '../../services/financingRows';
import { lastCommunicationFloor } from '../../services/pacQueueHydration';
import {
  isQueueFiltered,
  LEAD_STATUS_FILTER_OPTIONS,
  matchesPatientFilterTags,
  optionsFromRows,
  QUEUE_FILTER_ANY,
  QUEUE_FILTERS_OFF,
  QueueFilterValues,
  TRACK_FILTER_OPTIONS,
} from '../../services/queueFilters';
import { ListRangeControl } from '@/components/Hub/ListRangeControl';
import { QueueFilterBar } from '@/components/Hub/QueueFilterBar';
import { QueueFooter } from '@/components/Hub/QueueFooter';
import { QueueTable } from '../../components/QueueTable';
import { paymentStatusColumn } from './financingColumns';
import { lastContactColumn, patientColumn, paymentTrackColumn, pnColumn } from './hubColumns';
import { notesColumn } from './notesColumn';
import { statusColumn } from './statusColumn';
import { EditedRow, useHubRowEdits } from './useHubRowEdits';

/**
 * The Intake hub's Financing and approval tab (BAR-2829): patients waiting on
 * money, longest wait first. Membership and the filters run on the server on the
 * stage, `stage-day` and filter tags; paged 100 at a time with a true total.
 */

const DEFAULT_PRESET = 'all';

const ENTERED_PRESETS = [
  { id: 'all' as const, label: 'All time' },
  { id: 'last-30' as const, label: 'Last 30 days' },
  { id: 'last-60' as const, label: 'Last 60 days' },
];

const DEFAULTS: QueueFilterValues = { ...QUEUE_FILTERS_OFF, status: 'active' };

const STEP_FILTER_OPTIONS = [{ value: QUEUE_FILTER_ANY, label: 'All' }, ...FINANCING_STEP_OPTIONS];

type HubFinancingRow = EditedRow<FinancingLeadRow>;

const HYDRATION_NEEDS = {
  consult: true,
  procedure: true,
  callNotes: true,
  careTeam: true,
  eligibility: true,
  stickyNotes: true,
} as const;

const TAB_STYLE: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };

export interface FinancingTabProps {
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

export function FinancingTab({ now }: FinancingTabProps): ReactElement {
  const navigate = useNavigate();
  const resolvedNow = useNow(now);
  const today = useCalendarDay(resolvedNow);
  const { canViewPayments } = useCurrentUser().currentUserPermissions;

  const filters = usePatientListFilters<FinancingLeadRow>({
    resetKey: 'intake-financing',
    chipDefinitions: [],
    defaultPreset: DEFAULT_PRESET,
    now: resolvedNow,
  });

  const { values, setValues, setFilter, defaults, pnOptions } = useHubFilterValues(DEFAULTS);
  const filter = useMemo(() => buildFinancingFilter(filters.range, values), [filters.range, values]);
  const subscriptionParams = useMemo(() => financingSubscriptionParams(), []);

  const {
    rows: data,
    total,
    loading,
    error,
    page,
    setPage,
  } = useQueuePage<Patient>(FINANCING_PATIENTS_QUERY, 'Patient', filter, subscriptionParams);

  useEffect(() => {
    if (error && isMedplumRateLimitError(error)) {
      alert.warning({
        ...getRateLimitAlert(
          `The system temporarily limited how fast we can load the financing queue.
          Wait a few seconds before trying again.`
        ),
      });
    }
  }, [error]);

  const patients = useMemo(() => (error ? [] : data), [data, error]);
  const patientRefs = useMemo(
    () =>
      patients
        .map((patient) => (patient.id ? getReferenceString(patient) : undefined))
        .filter((reference): reference is string => reference !== undefined),
    [patients]
  );

  const hydration = usePacQueueHydration(
    patientRefs,
    { ...HYDRATION_NEEDS, lastCommunicationSince: lastCommunicationFloor(today) },
    `intake-financing:${filter}`
  );

  const builtRows = useMemo(
    () => buildFinancingRows(patients, hydration, resolvedNow, filters.range, canViewPayments),
    [patients, hydration, resolvedNow, filters.range, canViewPayments]
  );
  const edits = useHubRowEdits(builtRows, values.status);
  const { rows } = edits;

  // Rows that arrive by subscription skip the query's filter, so PN, track, location and step
  // are checked on their tags too. Status reads the row, so a change made here applies at once.
  const visibleRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          matchesPatientFilterTags(row.patient, { ...values, status: QUEUE_FILTER_ANY }) &&
          matchesFinancingStep(row.patient) &&
          (values.status === QUEUE_FILTER_ANY || row.activeLead.status === values.status)
      ),
    [rows, values]
  );
  const { markets } = useMarketOptions();
  const locationOptions = useMemo(() => optionsFromRows(markets, 'All'), [markets]);

  const { onNoteSaved, changeStatus, undoStatus } = edits;
  const columns = useMemo(
    () => [
      patientColumn<HubFinancingRow>(),
      paymentTrackColumn<HubFinancingRow>(),
      pnColumn<HubFinancingRow>(),
      paymentStatusColumn<HubFinancingRow>(),
      lastContactColumn<HubFinancingRow>(),
      notesColumn<HubFinancingRow>({ onSaved: onNoteSaved }),
      statusColumn<HubFinancingRow>({ onChange: changeStatus, onUndo: undoStatus, header: 'Lead status' }),
    ],
    [onNoteSaved, changeStatus, undoStatus]
  );

  return (
    <Box style={TAB_STYLE}>
      <QueueFilterBar
        date={
          <ListRangeControl
            label="Entered"
            presets={ENTERED_PRESETS}
            presetId={filters.presetId}
            value={filters.range}
            onChange={filters.setRange}
          />
        }
        filters={[
          { label: 'PN', value: values.pn, options: pnOptions, onChange: setFilter('pn') },
          { label: 'Payment track', value: values.track, options: TRACK_FILTER_OPTIONS, onChange: setFilter('track') },
          // The payment status is hidden without payments access, so its filter is too.
          ...(canViewPayments
            ? [{ label: 'Step', value: values.step, options: STEP_FILTER_OPTIONS, onChange: setFilter('step') }]
            : []),
          { label: 'Location', value: values.location, options: locationOptions, onChange: setFilter('location') },
          { label: 'Status', value: values.status, options: LEAD_STATUS_FILTER_OPTIONS, onChange: setFilter('status') },
        ]}
        isFiltered={isQueueFiltered(values, defaults) || filters.presetId !== DEFAULT_PRESET}
        onReset={() => {
          setValues(defaults);
          filters.setRange(DEFAULT_PRESET);
        }}
      />
      <QueueTable
        columns={columns}
        data={visibleRows}
        loading={loading || hydration.loading}
        initialSorting={[{ id: 'paymentStatus', desc: false }]}
        onOpenPatient={(patientId) => navigate(`/Patient/${patientId}/timeline`)}
        getRowClassName={edits.rowClassName}
      />
      <QueueFooter
        shown={visibleRows.length}
        total={total}
        page={page}
        pageCount={Math.ceil((total ?? 0) / QUEUE_PAGE_ROWS)}
        onPageChange={setPage}
      />
    </Box>
  );
}
